// frontend/src/pages/manufacturing/v2/components/ScrapComponentDialogV2.jsx
//
// "Scrap a part" for one step row on the shop-floor terminal.
//
// The operator ruined one part of the unit they are building (the top of a
// table) and will make another straight away. The dialog lists the step's
// components (from /material-requirements/) plus the unit itself, takes a
// quantity (pre-filled with one unit's worth) and a reason, previews what the
// remake will take out of stock, and posts to the WO scrap endpoint.
//
// What the server does (services/scrap.py): records the scrap and takes the
// replacement's RAW MATERIAL out of stock. The work order is untouched — it
// still owes the same good units and still issues its planned material at
// sign-off. No work order is created, revised or removed.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { toast } from 'react-toastify';

import { v2 } from '../../../../services/api';

const round3 = (v) => Math.round((parseFloat(v) || 0) * 1000) / 1000;

export function describeConsumption(consumes) {
  if (!consumes || consumes.length === 0) return '';
  return consumes
    .map((c) => `${round3(c.quantity)}${c.unit && c.unit !== 'EA' ? ` ${c.unit}` : ' ×'} ${c.item_code}`)
    .join(', ');
}

export default function ScrapComponentDialogV2({ open, step, onClose, onRecorded }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [itemId, setItemId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  // Load the step's components whenever the dialog opens for a step.
  useEffect(() => {
    if (!open || !step) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setReason('');
    setPreview(null);
    (async () => {
      try {
        const res = await v2.workOrders.materialRequirements(
          step.work_order_id, step.step_id ? { step: step.step_id } : {},
        );
        if (cancelled) return;
        const reqs = res.data?.requirements || [];
        const comps = reqs.map((r) => ({
          item_id: r.item_id,
          item_code: r.item_code,
          item_name: r.item_name,
          per_unit: round3(r.required),
          in_hand: r.in_hand != null ? round3(r.in_hand) : null,
          whole: false,
        }));
        const unit = step.output_item_id ? [{
          item_id: step.output_item_id,
          item_code: step.output_item_code,
          item_name: step.output_item_name,
          per_unit: 1,
          in_hand: null,
          whole: true,
        }] : [];
        const all = [...comps, ...unit];
        setOptions(all);
        const first = all[0];
        setItemId(first ? String(first.item_id) : '');
        setQuantity(first ? String(first.per_unit || 1) : '1');
      } catch (err) {
        console.error('ScrapComponentDialogV2: failed to load components', err);
        if (!cancelled) setLoadError('Could not load this step\'s components.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, step]);

  const selected = useMemo(
    () => options.find((o) => String(o.item_id) === String(itemId)) || null,
    [options, itemId],
  );

  // Preview what the remake consumes for the chosen part + quantity.
  useEffect(() => {
    if (!open || !selected) { setPreview(null); return undefined; }
    const qty = parseFloat(quantity);
    if (!(qty > 0)) { setPreview(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await v2.scrap.preview({ item: selected.item_id, quantity: qty });
        if (!cancelled) setPreview(res.data);
      } catch (err) {
        if (!cancelled) setPreview(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open, selected, quantity]);

  const handleSelect = (e) => {
    const next = options.find((o) => String(o.item_id) === e.target.value);
    setItemId(e.target.value);
    if (next) setQuantity(String(next.per_unit || 1));
  };

  const qtyNum = parseFloat(quantity);
  const canSubmit = !!selected && qtyNum > 0 && reason.trim().length > 0 && !busy;

  const submit = async () => {
    if (!canSubmit || !step) return;
    setBusy(true);
    try {
      const res = await v2.workOrders.scrap(step.work_order_id, {
        item: selected.item_id,
        quantity: String(qtyNum),
        step: step.step_id,
        notes: reason.trim(),
      });
      const consumed = describeConsumption(res.data?.consumed);
      toast.success(
        `Scrapped ${round3(qtyNum)} × ${selected.item_code}`
        + (consumed ? ` — took ${consumed} from stock` : ''),
      );
      if (onRecorded) onRecorded(res.data);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Failed to record scrap');
    } finally {
      setBusy(false);
    }
  };

  const consumption = describeConsumption(preview?.consumes);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Scrap a part{step ? ` · ${step.wo_number}` : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            For a part you ruined and will make again now. The work order is
            not changed — it still needs the same good units — and the raw
            material for the replacement comes out of stock.
          </Typography>

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading components…</Typography>
            </Box>
          )}
          {loadError && <Alert severity="error">{loadError}</Alert>}

          {!loading && !loadError && (
            <TextField
              select
              label="Part"
              value={itemId}
              onChange={handleSelect}
              fullWidth
              inputProps={{ 'data-testid': 'scrap-part-item' }}
              helperText={options.length === 0 ? 'This step has no components listed.' : undefined}
            >
              {options.map((o) => (
                <MenuItem key={o.item_id} value={String(o.item_id)} data-testid={`scrap-part-option-${o.item_id}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography variant="body2" fontWeight={600}>{o.item_code}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                      {o.item_name}
                    </Typography>
                    {o.whole ? (
                      <Chip size="small" label="whole unit" />
                    ) : (
                      <Chip size="small" variant="outlined" label={`${o.per_unit} per unit`} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            label="Quantity scrapped"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputProps={{ min: 0, step: 'any', 'data-testid': 'scrap-part-qty' }}
            fullWidth
          />
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ 'data-testid': 'scrap-part-reason' }}
            placeholder="e.g. cracked when clamping"
            fullWidth
            required
          />

          {selected && qtyNum > 0 && (
            <Alert severity="info" data-testid="scrap-part-preview">
              {preview?.bought_in ? (
                <>Will take <strong>{round3(qtyNum)} × {selected.item_code}</strong> from stock (bought in).</>
              ) : consumption ? (
                <>Will take <strong>{consumption}</strong> from stock to make it again.</>
              ) : (
                <>Working out what the remake needs…</>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={submit}
          disabled={!canSubmit}
          data-testid="scrap-part-submit"
        >
          {busy ? 'Recording…' : 'Scrap'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
